import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Users,
  ShoppingCart,
  BarChart3,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  Package,
  RefreshCw,
  Loader2,
  Sparkles
} from "lucide-react";

// Simple bar chart component
const SimpleBarChart = ({ data, dataKey, labelKey, color = "#8B5CF6", height = 200 }) => {
  if (!data || data.length === 0) return null;
  const maxValue = Math.max(...data.map(d => d[dataKey]));
  
  return (
    <div className="flex items-end justify-between gap-1" style={{ height }}>
      {data.map((item, idx) => {
        const barHeight = maxValue > 0 ? (item[dataKey] / maxValue) * 100 : 0;
        return (
          <div key={idx} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className="w-full rounded-t transition-all duration-300 hover:opacity-80"
              style={{ 
                height: `${barHeight}%`, 
                backgroundColor: color,
                minHeight: item[dataKey] > 0 ? '4px' : '0'
              }}
              title={`$${item[dataKey].toLocaleString()}`}
            />
            <span className="text-[10px] text-neutral-500 truncate w-full text-center">
              {item[labelKey]?.slice(-5) || ''}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const PaymentAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async (showRefreshToast = false) => {
    if (showRefreshToast) setRefreshing(true);
    try {
      const [analyticsRes, customersRes] = await Promise.all([
        authFetch(`${API}/payments/analytics/overview`),
        authFetch(`${API}/payments/analytics/customers`)
      ]);

      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }
      if (customersRes.ok) {
        setCustomers(await customersRes.json());
      }
      if (showRefreshToast) toast.success("Analytics refreshed");
    } catch (_error) {
      console.error("Error fetching analytics:", _error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12 text-neutral-500">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No analytics data available</p>
      </div>
    );
  }

  const { summary, product_performance, monthly_trend, daily_trend, recent_transactions } = analytics;
  const isGrowthPositive = summary.month_growth_percent >= 0;

  return (
    <div className="space-y-6" data-testid="payment-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">Revenue Analytics</h2>
          <p className="text-neutral-500">Track your payment performance and trends</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => fetchAnalytics(true)}
          disabled={refreshing}
          data-testid="refresh-analytics-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <Badge className="bg-purple-100 text-purple-700">All Time</Badge>
            </div>
            <p className="text-3xl font-bold text-neutral-900">
              ${summary.total_revenue.toLocaleString()}
            </p>
            <p className="text-sm text-neutral-500 mt-1">Total Revenue</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div className={`flex items-center text-sm ${isGrowthPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                {isGrowthPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {Math.abs(summary.month_growth_percent)}%
              </div>
            </div>
            <p className="text-3xl font-bold text-neutral-900">
              ${summary.this_month_revenue.toLocaleString()}
            </p>
            <p className="text-sm text-neutral-500 mt-1">This Month</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Percent className="w-5 h-5 text-blue-600" />
              </div>
              <Badge className="bg-blue-100 text-blue-700">Rate</Badge>
            </div>
            <p className="text-3xl font-bold text-neutral-900">
              {summary.conversion_rate}%
            </p>
            <p className="text-sm text-neutral-500 mt-1">Conversion Rate</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-amber-600" />
              </div>
              <Badge className="bg-amber-100 text-amber-700">Total</Badge>
            </div>
            <p className="text-3xl font-bold text-neutral-900">
              {summary.total_transactions}
            </p>
            <p className="text-sm text-neutral-500 mt-1">Transactions</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-neutral-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-neutral-500">Subscription Revenue</p>
                <p className="text-2xl font-bold text-purple-600">
                  ${summary.subscription_revenue.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="w-full bg-neutral-100 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full transition-all"
                style={{ 
                  width: `${summary.total_revenue > 0 ? (summary.subscription_revenue / summary.total_revenue) * 100 : 0}%` 
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-neutral-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-neutral-500">One-Time Purchases</p>
                <p className="text-2xl font-bold text-pink-600">
                  ${summary.one_time_revenue.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                <Package className="w-6 h-6 text-pink-600" />
              </div>
            </div>
            <div className="w-full bg-neutral-100 rounded-full h-2">
              <div 
                className="bg-pink-500 h-2 rounded-full transition-all"
                style={{ 
                  width: `${summary.total_revenue > 0 ? (summary.one_time_revenue / summary.total_revenue) * 100 : 0}%` 
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="trends" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="trends" data-testid="tab-trends">
            <TrendingUp className="w-4 h-4 mr-2" />
            Revenue Trends
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">
            <Package className="w-4 h-4 mr-2" />
            Products
          </TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">
            <CreditCard className="w-4 h-4 mr-2" />
            Recent Transactions
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers">
            <Users className="w-4 h-4 mr-2" />
            Top Customers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="trends">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Revenue Chart */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Monthly Revenue</CardTitle>
                <CardDescription>Last 6 months performance</CardDescription>
              </CardHeader>
              <CardContent>
                {monthly_trend.length > 0 ? (
                  <SimpleBarChart 
                    data={monthly_trend} 
                    dataKey="revenue" 
                    labelKey="month"
                    color="#8B5CF6"
                    height={180}
                  />
                ) : (
                  <p className="text-center text-neutral-400 py-8">No data yet</p>
                )}
              </CardContent>
            </Card>

            {/* Daily Revenue Chart */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Daily Revenue</CardTitle>
                <CardDescription>Last 30 days trend</CardDescription>
              </CardHeader>
              <CardContent>
                {daily_trend.length > 0 ? (
                  <SimpleBarChart 
                    data={daily_trend} 
                    dataKey="revenue" 
                    labelKey="date"
                    color="#EC4899"
                    height={180}
                  />
                ) : (
                  <p className="text-center text-neutral-400 py-8">No data yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="products">
          <Card className="border-neutral-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Product Performance</CardTitle>
              <CardDescription>Revenue breakdown by product</CardDescription>
            </CardHeader>
            <CardContent>
              {product_performance.length > 0 ? (
                <div className="space-y-4">
                  {product_performance.map((product, idx) => (
                    <div key={product.product_id} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          product.type === 'subscription' ? 'bg-purple-100' : 'bg-pink-100'
                        }`}>
                          {product.type === 'subscription' ? (
                            <CreditCard className={`w-5 h-5 text-purple-600`} />
                          ) : (
                            <Package className={`w-5 h-5 text-pink-600`} />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900">{product.name}</p>
                          <p className="text-sm text-neutral-500">
                            {product.count} sales • {product.type === 'subscription' ? 'Subscription' : 'One-time'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-neutral-900">
                          ${product.revenue.toLocaleString()}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {summary.total_revenue > 0 
                            ? `${((product.revenue / summary.total_revenue) * 100).toFixed(1)}% of total`
                            : '0%'
                          }
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-neutral-400 py-8">No product sales yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <Card className="border-neutral-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Recent Transactions</CardTitle>
              <CardDescription>Last 20 successful payments</CardDescription>
            </CardHeader>
            <CardContent>
              {recent_transactions.length > 0 ? (
                <div className="space-y-3">
                  {recent_transactions.map((tx, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900">{tx.user_email}</p>
                          <p className="text-xs text-neutral-500">{tx.product_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-neutral-900">${tx.amount.toFixed(2)}</p>
                        <p className="text-xs text-neutral-500">{tx.date}</p>
                        {(tx.discount_applied > 0 || tx.credit_applied > 0) && (
                          <p className="text-xs text-purple-500">
                            {tx.discount_applied > 0 && `−$${tx.discount_applied.toFixed(2)} discount`}
                            {tx.credit_applied > 0 && ` −$${tx.credit_applied.toFixed(2)} credit`}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-neutral-400 py-8">No transactions yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="customers">
          <Card className="border-neutral-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Top Customers</CardTitle>
              <CardDescription>Ranked by total spending</CardDescription>
            </CardHeader>
            <CardContent>
              {customers?.top_customers?.length > 0 ? (
                <div className="space-y-3">
                  {customers.top_customers.slice(0, 10).map((customer, idx) => (
                    <div key={customer.user_id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-neutral-400' : idx === 2 ? 'bg-amber-700' : 'bg-neutral-300'
                        }`}>
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-900">{customer.name}</p>
                          <p className="text-xs text-neutral-500">{customer.email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-neutral-900">${customer.total_spent.toFixed(2)}</p>
                        <p className="text-xs text-neutral-500">
                          {customer.transaction_count} transaction{customer.transaction_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-neutral-400 py-8">No customer data yet</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PaymentAnalytics;
